import {
    Button,
    Card,
    Checkbox,
    Col,
    Collapse, Dropdown,
    Form,
    Icon,
    List, Menu,
    Modal,
    Row,
    Spin,
    TimePicker,
    Tooltip,
    Typography
} from "antd";
import {FormComponentProps} from "antd/lib/form";
import {PaxNotifierPaxRecord} from "../../types";
import React, {useContext} from "react";
import {OrgDataContext} from "../../../../../context/orgData";
import {Moment} from "moment-timezone";
import {arrayToMap, arrayToMapAccumulate} from "../../../../../common/util-ts";
import {Location, LocationFragmentFragment} from "../../../../../__generated__/graphql/types";
import {CheckInPaxNotifierContext} from "./checkin-pax-notifier";
import moment from "moment";

export type FormValues = {
    requestedCheckInTime: Moment;
}

export type PaxNotifierFormOffsetOptions = {
    offsetMinutesOptions?: number[];
}

export type PaxNotifierFormProps = {
    /**
     * If set, pre-fill the expected check-in time field with this value and disable user input.
     */
    requestedCheckInTime?: Moment,

    paxDocs: PaxNotifierPaxRecord[],
    scheduledFlightDate?: Moment,
} & PaxNotifierFormOffsetOptions

function PaxNotifierForm(props: PaxNotifierFormProps){

    const orgData = useContext(OrgDataContext);
    const context = useContext(CheckInPaxNotifierContext);
    const tpName = orgData?.transporter?.name;

    const form = (props as FormComponentProps & PaxNotifierFormProps).form;

    const anySelected = context.state.selectedPaxIds.length > 0;

    function submit(){
        form.validateFieldsAndScroll((err, values) => {
            if (!err){
                if (context.selectedPaxWithNotifications?.length > 0){
                    Modal.confirm({
                        title: 'Confirm Notify Passengers',
                        content: `You are about to re-notify ${context.selectedPaxWithNotifications.length} passenger(s) who have already received check-in notifications. Do you want to proceed?`,
                        okText: 'Yes',
                        cancelText: 'No',
                        onOk: () => {
                            context.onFormSubmit(values);
                        }
                    })
                }
                else {
                    context.onFormSubmit(values);
                }
            }
        })
    }

    function isSelected(paxId: string){
        return context.state.selectedPaxIds.includes(paxId);
    }

    function toggleSelection(pax: PaxNotifierPaxRecord){
        const currentlySelected = isSelected(pax._id);
        let newSelectedPaxIds: string[];
        if (currentlySelected){
            newSelectedPaxIds = context.state.selectedPaxIds.filter(id => id !== pax._id);
        }
        else{
            newSelectedPaxIds = [ ...context.state.selectedPaxIds, pax._id ];
        }
        context.setSelectedPaxIds(newSelectedPaxIds);
    }

    function toggleSelectDeselectAll(){
        let newSelectedPaxIds: string[];
        if (anySelected){
            newSelectedPaxIds = [];
        }
        else{
            newSelectedPaxIds = props.paxDocs.map(pax => pax._id);
        }
        context.setSelectedPaxIds(newSelectedPaxIds);
    }

    function selectAllDeparture(departureId: string){
        const paxToSelect = props.paxDocs
            .filter(pax => pax.departureID?._id === departureId)
            .map(pax => pax._id);

        const newSelectedPaxIds = Array.from(new Set([
            ...context.state.selectedPaxIds,
            ...paxToSelect
        ]));

        context.setSelectedPaxIds(newSelectedPaxIds);
    }

    function deselectAllDeparture(departureId: string){
        const paxToDeselect = props.paxDocs
            .filter(pax => pax.departureID?._id === departureId)
            .map(pax => pax._id);

        const newSelectedPaxIds = context.state.selectedPaxIds
            .filter(id => !paxToDeselect.includes(id));

        context.setSelectedPaxIds(newSelectedPaxIds);
    }

    function toggleAllDeparture(departureId: string){
        const groupPax = props.paxDocs.filter(pax => pax.departureID?._id === departureId);
        const allSelectedInGroup = groupPax.every(pax => isSelected(pax._id));
        if (allSelectedInGroup){
            deselectAllDeparture(departureId);
        }
        else{
            selectAllDeparture(departureId);
        }
    }

    function getDepartureAllSelected(departureId: string): { checked: boolean, indeterminate: boolean }{
        const groupPax = props.paxDocs.filter(pax => pax.departureID?._id === departureId);
        const selectedInGroup = groupPax.filter(pax => isSelected(pax._id));
        if (selectedInGroup.length === 0){
            return { checked: false, indeterminate: false };
        }
        else if (selectedInGroup.length === groupPax.length){
            return { checked: true, indeterminate: false };
        }
        else{
            return { checked: true, indeterminate: true };
        }
    }

    function renderListItem(pax: PaxNotifierPaxRecord){
        const checkInTime = pax.requestedCheckInTime ?
            moment(pax.requestedCheckInTime, 'HH:mm:ss').format('HH:mm') :
            null;

        const receivedMessageTs = pax.lastCheckInNotificationEvent?.createdTs ? (
            moment(pax.lastCheckInNotificationEvent?.createdTs).format('MM/DD/YY HH:mm')
        ) : null;

        const errorMessage = pax.lastCheckInNotificationEvent?.success === false ?
            pax.lastCheckInNotificationEvent.errorMessage :
            null;

        let statusText: React.ReactNode;

        if (errorMessage){
            statusText = (
                <Tooltip title={
                    <Typography.Text type="danger">{errorMessage}</Typography.Text>
                }>
                    <Typography.Text type="danger">ERROR <Icon type="down" /></Typography.Text>
                </Tooltip>
            )
        }
        else if (checkInTime && receivedMessageTs){
            statusText = <span>Notified at {receivedMessageTs} for <Typography.Text type="warning">{checkInTime}</Typography.Text></span>;
        }
        else {
            statusText = <Typography.Text type="secondary">Not notified</Typography.Text>;
        }

        return (
            <List.Item key={pax._id}>
                <Row type="flex" align="middle" style={{ width: '100%' }}>
                    <Col>
                        <Checkbox
                            checked={isSelected(pax._id)}
                            onChange={() => toggleSelection(pax)}
                            style={{ width: '100%', display: 'block' }}
                        >{pax.lastName}, {pax.firstName}</Checkbox>
                    </Col>
                    <Col style={{ marginLeft: 'auto', textAlign: 'right' }}>
                        {statusText}
                    </Col>
                </Row>
            </List.Item>
        )
    }

    const sortedPaxDocs = [ ...props.paxDocs ].sort((a, b) => {
        const lastNameComp = a.lastName.localeCompare(b.lastName);
        if (lastNameComp !== 0){
            return lastNameComp;
        }
        return a.firstName.localeCompare(b.firstName);
    });

    const groupByDeparture =
        arrayToMapAccumulate<string, PaxNotifierPaxRecord>(
            sortedPaxDocs, p => p.departureID?._id || 'undefined', p => p
        );

    const departList = sortedPaxDocs.map(p => p.departureID);

    const departureIdToNameMap = arrayToMap<string, Location, string>(
        departList, "_id", l => l.name
    );

    const sortedDepartures = Array.from(groupByDeparture.keys()).sort((a, b) => {
        const nameA = departureIdToNameMap.get(a) || '';
        const nameB = departureIdToNameMap.get(b) || '';
        return nameA.localeCompare(nameB);
    });

    const formattedSfd = props.scheduledFlightDate?.format('dddd, MMMM d, YYYY') || 'Unknown Date';
    const formattedTime = form.getFieldValue('requestedCheckInTime')?.format('hh:mm A') || 'Unknown Time';
    let msgDepartLocation = '{Depart Location}';

    if (sortedDepartures.length === 1){
        const departLocation = departList.find(l => l?._id === sortedDepartures[0]);
        if (departLocation?.name){
            msgDepartLocation = departLocation.name;
        }
    }

    const message = <span>🚁 Hello {'{First Name}'} {'{Last Name}'},<br /><br />
        Please check in at {tpName} {msgDepartLocation} for your {formattedSfd} flight by {formattedTime} CST
    </span>

    function renderGroup(departureId: string) {
        const paxInGroup = groupByDeparture.get(departureId) || [];

        return (
            <>
                <List
                    dataSource={paxInGroup}
                    renderItem={renderListItem}
                    size="small"
                />
            </>
        )
    }

    function renderGroups(){
        return <Collapse defaultActiveKey={sortedDepartures}>
            {sortedDepartures.map((departureId) => (
                <Collapse.Panel
                    key={departureId}
                    header={
                        <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            {...getDepartureAllSelected(departureId)}
                            onClick={(e) => {
                                toggleAllDeparture(departureId);
                            }}
                        >
                            <span>{departureIdToNameMap.get(departureId) || 'Unknown Location'}</span>
                            <span> ({groupByDeparture.get(departureId).length} PAX)</span>
                        </Checkbox>
                        </span>
                    }
                >
                    {renderGroup(departureId)}
                </Collapse.Panel>
            ))}
        </Collapse>
    }

    const isSubmitting = context.sendCheckInNotificationResult?.loading;

    return (
        <Spin spinning={isSubmitting} indicator={<Icon type="loading" />}>
            <Form>
                <Form.Item label="Expected Check-In Time (CST)">
                    <div>
                        {form.getFieldDecorator('requestedCheckInTime', {
                            rules: [{ required: true, message: 'Please input expected check-in time (CST)' }],
                            initialValue: props.requestedCheckInTime
                        })(
                            <TimePicker format="HH:mm" />
                        )}
                    </div>
                    {props.offsetMinutesOptions?.length ? (
                        <Row type="flex" gutter={6}>
                            {props.offsetMinutesOptions.map((offset) => (
                                <Col>
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            let baseTime: Moment = form.getFieldValue('requestedCheckInTime');
                                            if (!baseTime) {
                                                baseTime = moment();
                                            }
                                            form.setFieldsValue({
                                                requestedCheckInTime: baseTime.clone().add(offset, 'minutes')
                                            });
                                        }}
                                    >{offset} mins</Button>
                                </Col>
                            ))}
                        </Row>
                    ) : null}
                </Form.Item>
                <Row type="flex" style={{ marginBottom: 12 }}>
                    <Col>Passengers to Notify</Col>
                    <Col style={{ marginLeft: 'auto' }}>
                        <Dropdown
                            overlay={
                                <Menu>
                                    <Menu.Item
                                        key="select-all"
                                        onClick={() => {
                                            const allPaxIds = props.paxDocs.map(pax => pax._id);
                                            context.setSelectedPaxIds(allPaxIds);
                                        }}
                                    >
                                        Select All
                                    </Menu.Item>
                                    <Menu.Item
                                        key="deselect-all"
                                        onClick={() => {
                                            context.setSelectedPaxIds([]);
                                        }}
                                    >
                                        Deselect All
                                    </Menu.Item>
                                    <Menu.Item
                                        key="select-only-non-notified"
                                        onClick={() => {
                                            const nonNotifiedPaxIds = props.paxDocs
                                                .filter(pax => !pax.lastCheckInNotificationEvent.success)
                                                .map(pax => pax._id);
                                            context.setSelectedPaxIds(nonNotifiedPaxIds);
                                        }}
                                    >
                                        Select Only Non-Notified
                                    </Menu.Item>
                                </Menu>
                            }
                        >
                            <Button
                                type="link"
                                size="small"
                                onClick={toggleSelectDeselectAll}
                            >
                                {anySelected ? 'Deselect All' : 'Select All'} <Icon type="down" />
                            </Button>
                        </Dropdown>
                    </Col>
                </Row>
                <Form.Item>
                    {renderGroups()}
                </Form.Item>
                <Form.Item>
                    <Card title="SMS Preview" size="small">
                        {message}
                    </Card>
                </Form.Item>
                <Form.Item>
                    <Button
                        disabled={!context.state.selectedPaxIds.length || isSubmitting}
                        type="primary"onClick={submit}>
                        {context.state.selectedPaxIds.length ? `Notify ${context.state.selectedPaxIds.length} PAX` : 'Notify PAX'} via SMS
                    </Button>
                </Form.Item>
            </Form>
        </Spin>
    )
}

export default Form.create<FormComponentProps & PaxNotifierFormProps>()(PaxNotifierForm)